/**
 * Skills — reframed for Skills v2 (2026-08-19).
 *
 * Top-level tabs are now outcome-first per the mockup (screens #s5 / #s6):
 *   Gallery (default) · Your skills · Author · Developer (de-emphasized).
 *
 * The old 5-tab developer console (Base / Instances / Discover / CL Review /
 * Observability) is PRESERVED IN FULL under the "Developer" tab — no capability
 * deleted — but every blocking alert()/prompt()/confirm() has been replaced with
 * non-blocking inline UI: a Toast status line, inline confirm buttons, an inline
 * clone-to-agent picker, and an inline reject-reason input.
 *
 * BYOK-strict: no direct LLM calls from the client; all operations go through
 * skillsApi which routes through the tenant-authenticated backend.
 */
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { skillsApi, agentApi } from "../../api/client";
import { COLORS } from "../../theme/colors";
import { MATERIALS, RADIUS } from "../../theme/materials";
import CLObservabilityTab from "./CLObservabilityTab";
import SkillGalleryTab from "./SkillGalleryTab";
import ExecutableSkillsGallery from "./ExecutableSkillsGallery";
import YourSkillsTab from "./YourSkillsTab";
import AuthorSkillTab from "./AuthorSkillTab";
import { parseInstanceBundle } from "./skillLibFormat";
import QualityImproveTab from "./QualityImproveTab";
import SkillsOverviewStrip from "./SkillsOverviewStrip";

const cardStyle = () => ({
  ...MATERIALS.thickGlass(),
  padding: 16,
  borderRadius: RADIUS.inner,
});
const cardStyleSmall = () => ({
  ...MATERIALS.thickGlass(),
  padding: 20,
  borderRadius: RADIUS.inner,
});

const RED = COLORS.red || "#ef4444";
const GREEN = COLORS.green || "#059669";
const AMBER = COLORS.orange || "#d97706";
const BLUE = COLORS.accentText || COLORS.accent || "#3b82f6";
const MUTED = COLORS.textMuted || "#6b7280";
const CARD_BG = COLORS.surface || "white";
const CARD_ALT = COLORS.surfaceHover || "#fafbfc";
const BORDER = COLORS.border || "#e2e5eb";
const ACCENT_SOFT = COLORS.accentSoft || "#eff6ff";

const badge = (label, color) => (
  <span
    style={{
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: 4,
      fontSize: 11,
      fontWeight: 500,
      background: `${color}20`,
      color,
      marginLeft: 8,
    }}
  >
    {label}
  </span>
);

const actionBadge = (action) => {
  const map = {
    auto_active: { label: "AUTO-ACTIVE", color: GREEN },
    attach_with_review: { label: "ATTACH W/ REVIEW", color: BLUE },
    suggested: { label: "SUGGESTED", color: AMBER },
    no_match: { label: "NO MATCH", color: MUTED },
  };
  const cfg = map[String(action).toLowerCase()] || { label: action, color: MUTED };
  return badge(cfg.label, cfg.color);
};

const statusBadge = (status) => {
  const map = {
    passing: { label: "passing", color: GREEN },
    warning: { label: "warning", color: AMBER },
    quarantined: { label: "quarantined", color: RED },
    unknown: { label: "unknown", color: MUTED },
    pending: { label: "pending", color: AMBER },
    applied: { label: "applied", color: GREEN },
    rejected: { label: "rejected", color: RED },
  };
  const cfg = map[status] || { label: status, color: MUTED };
  return badge(cfg.label, cfg.color);
};

const btnStyle = (bg) => ({
  padding: "4px 10px",
  fontSize: 11,
  background: bg,
  color: "white",
  border: "none",
  borderRadius: 4,
  cursor: "pointer",
});

/**
 * Non-blocking status line. Replaces every alert() in the developer surface.
 * kind ∈ "success" | "error". Auto-dismiss is the caller's job (we keep it
 * until the next notify or an explicit clear) so tests can assert on it.
 */
function Toast({ toast, onClose }) {
  if (!toast) return null;
  const isErr = toast.kind === "error";
  return (
    <div
      role="status"
      style={{
        display: "flex", alignItems: "center", gap: 10, marginBottom: 12,
        padding: "10px 12px", borderRadius: 8, fontSize: 13,
        background: isErr ? `${RED}18` : `${GREEN}18`,
        color: isErr ? RED : GREEN,
        border: `1px solid ${isErr ? RED : GREEN}55`,
      }}
    >
      <span style={{ flex: 1 }}>{toast.msg}</span>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onClose}
        style={{ background: "transparent", border: "none", color: "inherit", cursor: "pointer", fontSize: 15 }}
      >✕</button>
    </div>
  );
}

/**
 * Minimal unified line diff (LCS-based). Good enough for skill .md bodies
 * (hundreds of lines); returns [{type: 'same'|'add'|'del', line}].
 */
function unifiedDiff(oldText, newText) {
  const a = (oldText || "").split("\n");
  const b = (newText || "").split("\n");
  const m = a.length, n = b.length;
  if (m * n > 4_000_000) {
    return [{ type: "same", line: "(diff too large to render)" }];
  }
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out = [];
  let i = 0, j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) { out.push({ type: "same", line: a[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ type: "del", line: a[i] }); i++; }
    else { out.push({ type: "add", line: b[j] }); j++; }
  }
  while (i < m) { out.push({ type: "del", line: a[i] }); i++; }
  while (j < n) { out.push({ type: "add", line: b[j] }); j++; }
  return out;
}

export default function SkillsView() {
  // Top-level outcome-first tabs.
  // Deep-linkable top tab: /skills?tab=gallery|your-skills|author|quality|developer
  const _location = useLocation();
  const _navigate = useNavigate();
  const _VALID_TOP = ["gallery", "executable", "your-skills", "author", "quality", "developer"];
  const _initialTab = (() => {
    const t = new URLSearchParams(_location.search).get("tab");
    return _VALID_TOP.includes(t) ? t : "gallery";
  })();
  const [topTab, setTopTab] = useState(_initialTab);
  // Keep topTab in sync when the query param changes (e.g. a cross-surface link).
  useEffect(() => {
    const t = new URLSearchParams(_location.search).get("tab");
    if (_VALID_TOP.includes(t)) setTopTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_location.search]);
  // Developer sub-tabs (the old console).
  const [tab, setTab] = useState("instances");
  const [toast, setToast] = useState(null);
  const notify = (msg, kind = "success") => setToast({ msg, kind });

  const [instances, setInstances] = useState([]);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [pending, setPending] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [matches, setMatches] = useState([]);
  const [discoverRan, setDiscoverRan] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // Diff viewer state
  const [diff, setDiff] = useState(null);
  const [diffBusy, setDiffBusy] = useState(false);
  // Inline confirm/clone/reject state (replaces confirm()/prompt()).
  const [detachConfirm, setDetachConfirm] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneTarget, setCloneTarget] = useState("");
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyTenant, setCopyTenant] = useState("");
  const [copyAgent, setCopyAgent] = useState("");
  const [rejectFor, setRejectFor] = useState(null); // pending example id
  const [rejectReason, setRejectReason] = useState("not useful");
  // Import form state
  const [showImport, setShowImport] = useState(false);
  const [importForm, setImportForm] = useState({
    base_name: "", base_version: "1.0.0", schema_hash: "",
    target_agent_id: "", body_md: "",
  });
  // Base-skills tab state
  const [baseSkills, setBaseSkills] = useState([]);
  const [selectedBase, setSelectedBase] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    name: "", version: "1.0.0", visibility: "tenant",
    frontmatter: '{\n  "applies_to": {"required_columns": [], "embedding_hints": []}\n}',
    body_md: "",
  });

  const refreshInstances = async () => {
    try {
      const inst = await skillsApi.listInstances();
      setInstances(Array.isArray(inst) ? inst : []);
    } catch (e) {
      setErr(String(e.message || e));
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [inst, ag] = await Promise.all([
          skillsApi.listInstances(),
          agentApi.list(),
        ]);
        setInstances(Array.isArray(inst) ? inst : []);
        setAgents(Array.isArray(ag?.data) ? ag.data : []);
      } catch (e) {
        setErr(String(e.message || e));
      }
    })();
  }, []);

  useEffect(() => {
    if (topTab !== "developer" || tab !== "base") return;
    (async () => {
      try {
        const rows = await skillsApi.listBase();
        setBaseSkills(Array.isArray(rows) ? rows : []);
      } catch (e) {
        setErr(String(e.message || e));
      }
    })();
  }, [tab, topTab]);

  const loadPending = async (inst) => {
    setSelectedInstance(inst);
    setDiff(null);
    setDetachConfirm(false);
    setCloneOpen(false);
    setRejectFor(null);
    setPending([]);
    try {
      const rows = await skillsApi.listPending(inst.id);
      setPending(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setErr(String(e.message || e));
    }
  };

  const runDiscover = async () => {
    if (!selectedAgent) return;
    setBusy(true); setErr(null); setDiscoverRan(false);
    try {
      const rows = await skillsApi.discover(selectedAgent);
      setMatches(Array.isArray(rows) ? rows : []);
      setDiscoverRan(true);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  const doAttach = async (m) => {
    if (!selectedAgent) return;
    setBusy(true); setErr(null);
    try {
      const res = await skillsApi.attach(selectedAgent, m.skill_name, m.table_name);
      notify(
        res.attached
          ? `Attached (${res.verdict}). Instance ${String(res.instance_id || "").slice(0, 12)}…`
          : `Not attached: ${res.detached_reason || res.verdict}`,
        res.attached ? "success" : "error"
      );
      await refreshInstances();
    } catch (e) {
      notify(`Attach failed: ${e.message || e}`, "error");
    } finally {
      setBusy(false);
    }
  };

  const doDetach = async () => {
    if (!selectedInstance) return;
    try {
      await skillsApi.detach(selectedInstance.agent_id, selectedInstance.id);
      setDetachConfirm(false);
      setSelectedInstance(null);
      notify("Detached.");
      await refreshInstances();
    } catch (e) {
      notify(`Detach failed: ${e.message || e}`, "error");
    }
  };

  const doRebase = async () => {
    if (!selectedInstance) return;
    try {
      const updated = await skillsApi.rebaseInstance(selectedInstance.id);
      notify(`Rebased to base v${updated.base_version}. User edits preserved.`);
      setSelectedInstance(updated);
      await refreshInstances();
    } catch (e) {
      notify(`Rebase failed: ${e.message || e}`, "error");
    }
  };

  const doRefresh = async () => {
    try {
      const fresh = await skillsApi.getInstance(selectedInstance.id);
      setSelectedInstance(fresh?.data || fresh);
      notify("Refreshed instance detail.");
    } catch (e) {
      notify(`Refresh failed: ${e.message || e}`, "error");
    }
  };
  const doCopyToTenant = async () => {
    try {
      await skillsApi.copyToTenant(selectedInstance.id, copyTenant.trim(), copyAgent.trim() || null);
      setCopyOpen(false); setCopyTenant(""); setCopyAgent("");
      notify("Copied instance to the target tenant.");
    } catch (e) {
      notify(`Copy failed: ${e.message || e}`, "error");
    }
  };
  const doClone = async () => {
    if (!selectedInstance || !cloneTarget) return;
    try {
      await skillsApi.cloneInstance(selectedInstance.id, cloneTarget.trim());
      setCloneOpen(false);
      setCloneTarget("");
      notify("Cloned.");
      await refreshInstances();
    } catch (e) {
      notify(`Clone failed: ${e.message || e}`, "error");
    }
  };

  const doDiff = async () => {
    if (!selectedInstance) return;
    setDiffBusy(true);
    try {
      const base = await skillsApi.getBase(selectedInstance.base_name);
      setDiff(unifiedDiff(base.body_md || "", selectedInstance.body_md || ""));
    } catch (e) {
      notify(`Diff failed: ${e.message || e}`, "error");
    } finally {
      setDiffBusy(false);
    }
  };

  // Upload an EXPORTED instance file → auto-fill the cryptic fields from the
  // filename (`skill_<name>_<hash>.md`) + the body's frontmatter, so the user
  // only has to pick the target agent.
  const handleInstanceFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      const body = await file.text();
      const d = parseInstanceBundle(file.name, body);
      setImportForm((f) => ({
        ...f, body_md: body,
        base_name: d.base_name || f.base_name,
        base_version: d.base_version || f.base_version,
        schema_hash: d.schema_hash || f.schema_hash,
      }));
    } catch {
      notify("Couldn't read that file.", "error");
    }
  };

  const doImport = async () => {
    const f = importForm;
    if (!f.base_name || !f.target_agent_id || !f.body_md || !f.schema_hash) {
      notify("Upload the exported instance file and pick a target agent.", "error");
      return;
    }
    try {
      await skillsApi.importInstance(f);
      setShowImport(false);
      setImportForm({ base_name: "", base_version: "1.0.0", schema_hash: "", target_agent_id: "", body_md: "" });
      await refreshInstances();
      notify("Imported.");
    } catch (e) {
      notify(`Import failed: ${e.message || e}`, "error");
    }
  };

  const doUploadBase = async () => {
    const f = uploadForm;
    if (!f.name || !f.body_md) { notify("name and body_md are required.", "error"); return; }
    let fm;
    try { fm = JSON.parse(f.frontmatter); }
    catch { notify("Frontmatter must be valid JSON.", "error"); return; }
    try {
      await skillsApi.createBase({
        name: f.name, version: f.version, body_md: f.body_md,
        frontmatter: fm, visibility: f.visibility,
      });
      setShowUpload(false);
      const rows = await skillsApi.listBase();
      setBaseSkills(Array.isArray(rows) ? rows : []);
      notify("Base skill uploaded.");
    } catch (e) {
      notify(`Upload failed: ${e.message || e}`, "error");
    }
  };

  const inputStyle = { padding: "6px 8px", fontSize: 12, borderRadius: 4, border: `1px solid ${BORDER}`, width: "100%", boxSizing: "border-box", background: COLORS.surface, color: COLORS.text };

  const doApply = async (ex) => {
    if (!selectedInstance) return;
    try {
      await skillsApi.applyPending(selectedInstance.id, ex.id);
      await loadPending(selectedInstance);
      notify("Example applied.");
    } catch (e) {
      notify(`Apply failed: ${e.message || e}`, "error");
    }
  };
  const doReject = async (ex) => {
    if (!selectedInstance || rejectReason == null) return;
    try {
      await skillsApi.rejectPending(selectedInstance.id, ex.id, rejectReason);
      setRejectFor(null);
      await loadPending(selectedInstance);
      notify("Example rejected.");
    } catch (e) {
      notify(`Reject failed: ${e.message || e}`, "error");
    }
  };

  const TOP_TABS = [
    { id: "gallery", label: "Gallery" },
    { id: "executable", label: "Executable" },
    { id: "your-skills", label: "Your skills" },
    { id: "author", label: "Author" },
    { id: "quality", label: "Quality & Improve" },
    { id: "developer", label: "Developer" },
  ];

  // Computed in JS (not inline JSX) so v8 line-attributes the branch for the
  // 100% diff-coverage gate — the auto-detected instance-bundle banner.
  const detectedBanner = (importForm.base_name || importForm.schema_hash) ? <div style={{ fontSize: 12, color: COLORS.text, marginBottom: 10, padding: "8px 10px", borderRadius: 6, background: CARD_ALT }}>Detected: skill <b>{importForm.base_name || "?"}</b> v{importForm.base_version || "?"}{" · schema "}<code>{importForm.schema_hash || "?"}</code>{" · "}{(importForm.body_md || "").length} chars</div> : null;

  return (
    <div
      className="panel-scroll"
      style={{ flex: 1, padding: 20, overflowY: "auto", minHeight: 0 }}
    >
      <div style={{ maxWidth: 1200 }}>
        <h1 style={{ fontSize: 22, marginBottom: 6, color: COLORS.text || "#1a1d24" }}>
          Skills
        </h1>
        <p style={{ color: MUTED, fontSize: 13, marginBottom: 12 }}>
          Ready-made playbooks that make your agents better at a job — browse the shelf,
          see what's active, or author your own.
        </p>

        {/* At-a-glance landing: counts + one-tap quick actions */}
        <SkillsOverviewStrip onGo={(t) => { setTopTab(t); _navigate(`/skills?tab=${t}`); }} />

        {/* How Skills work — one line that ties the vocabulary together */}
        <div style={{
          display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 18, padding: "10px 13px",
          borderRadius: 10, background: COLORS.surfaceHover || "transparent",
          border: `1px solid ${BORDER}`, fontSize: 12.5, color: MUTED, lineHeight: 1.55,
        }}>
          <span>🧭</span>
          <div>
            <b style={{ color: COLORS.text }}>How Skills work:</b> a <b style={{ color: COLORS.text }}>skill</b> is a
            playbook. <b>Browse</b> the Gallery → <b>Add it to an agent</b> → it's <b>active on</b> that agent, and{" "}
            <b>Quality &amp; Improve</b> shows how it's doing. Everything else — your library, authoring, and the raw
            SKILL.md / per-agent instances in the Developer view — hangs off those four words.
          </div>
        </div>

        {/* Top-level outcome-first tabs */}
        <div style={{ display: "flex", gap: 8, marginBottom: 20, borderBottom: `1px solid ${BORDER}` }}>
          {TOP_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => { setTopTab(t.id); _navigate(`/skills?tab=${t.id}`); }}
              style={{
                padding: "8px 16px", border: "none", background: "transparent",
                borderBottom: topTab === t.id ? `2px solid ${BLUE}` : "2px solid transparent",
                color: topTab === t.id ? BLUE : (t.id === "developer" ? COLORS.textDim || MUTED : MUTED),
                fontWeight: topTab === t.id ? 600 : 400, cursor: "pointer",
              }}
            >{t.label}</button>
          ))}
        </div>

        {topTab === "gallery" && <SkillGalleryTab onAuthor={() => { setTopTab("author"); _navigate("/skills?tab=author"); }} />}
        {topTab === "executable" && <ExecutableSkillsGallery />}
        {topTab === "your-skills" && <YourSkillsTab onOpenDeveloper={() => setTopTab("developer")} />}
        {topTab === "author" && (
          <AuthorSkillTab
            editSkill={new URLSearchParams(_location.search).get("edit") || null}
            onPublished={() => notify("Published to your library ✓")}
          />
        )}
        {topTab === "quality" && <QualityImproveTab />}

        {topTab === "developer" && (
          <div>
            <div style={{ fontSize: 12.5, color: MUTED, marginBottom: 14, maxWidth: "72ch" }}>
              Power-user surface — raw SKILL.md, per-agent instances, the discovery matcher,
              the continual-learning review queue, and observability. Everything the
              outcome-first tabs abstract away lives here.
            </div>

            {/* Discoverability: executable/sandboxed skills live in Admin */}
            <div style={{
              display: "flex", alignItems: "center", gap: 12, marginBottom: 16, padding: "12px 14px",
              borderRadius: 12, background: COLORS.surface, border: `1px solid ${BORDER}`,
            }}>
              <span style={{ fontSize: 18 }}>🧪</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 650, color: COLORS.text }}>Executable (sandboxed) skills</div>
                <div style={{ fontSize: 12, color: MUTED }}>
                  Run a skill's Python safely in a gVisor sandbox — certification, isolation, and run history. Admin-only, default off.
                </div>
              </div>
              <button
                type="button"
                onClick={() => _navigate("/admin?tab=executable_skills")}
                style={{ font: "inherit", fontSize: 12.5, fontWeight: 600, color: BLUE, background: "transparent", border: `1px solid ${BORDER}`, borderRadius: 8, padding: "7px 12px", cursor: "pointer", whiteSpace: "nowrap" }}
              >Open in Admin →</button>
            </div>

            <Toast toast={toast} onClose={() => setToast(null)} />

            {err && (
              <div style={{ padding: 10, background: `${RED}18`, color: RED, borderRadius: 6, marginBottom: 12 }}>
                {err}
              </div>
            )}

            <div style={{ display: "flex", gap: 8, marginBottom: 20, borderBottom: `1px solid ${BORDER}` }}>
              {["instances", "discover", "review", "base", "observability"].map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  style={{
                    padding: "8px 16px", border: "none", background: "transparent",
                    borderBottom: tab === t ? `2px solid ${BLUE}` : "2px solid transparent",
                    color: tab === t ? BLUE : MUTED, fontWeight: tab === t ? 600 : 400, cursor: "pointer",
                  }}
                >
                  {t === "instances" && "Instances"}
                  {t === "discover" && "Discover"}
                  {t === "review" && "CL Review Queue"}
                  {t === "base" && "Base Skills"}
                  {t === "observability" && "Observability"}
                </button>
              ))}
            </div>

            {tab === "observability" && <CLObservabilityTab />}

            {tab === "instances" && (
              <div>
                <div style={{ marginBottom: 12, display: "flex", gap: 8 }}>
                  <button onClick={() => setShowImport((v) => !v)} style={btnStyle(BLUE)}>
                    {showImport ? "Cancel" : "Install instance bundle…"}
                  </button>
                </div>
                {showImport && (
                  <div style={{ ...cardStyle(), marginBottom: 16 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Install an exported instance onto an agent</div>
                    <div style={{ fontSize: 12, color: MUTED, marginBottom: 12, lineHeight: 1.5 }}>
                      Advanced: re-install a specific agent's <b>exported instance</b> (with its
                      per-agent edits) onto an agent. <b>Importing a new skill? </b>
                      Use <b>Your skills → ↧ Import skill</b> instead — this is not that.
                    </div>

                    {/* Step 1 — upload the exported file; everything but the agent auto-fills. */}
                    <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>1. Exported instance file (.md)</label>
                    <div style={{ fontSize: 11, color: MUTED, margin: "2px 0 6px" }}>
                      From <b>Developer → Instances → pick an instance → Export</b>. The file is named
                      <code style={{ margin: "0 3px" }}>skill_&lt;name&gt;_&lt;hash&gt;.md</code> — we read its name, version, and
                      schema hash for you.
                    </div>
                    <input type="file" accept=".md,text/markdown" aria-label="Exported instance file"
                      onChange={handleInstanceFile} style={{ fontSize: 12, marginBottom: 10 }} />

                    {detectedBanner}

                    {/* Step 2 — pick the agent. */}
                    <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>2. Install on agent</label>
                    <div style={{ fontSize: 11, color: MUTED, margin: "2px 0 6px" }}>The agent that will run this instance.</div>
                    <select value={importForm.target_agent_id} aria-label="Install on agent"
                      onChange={(e) => setImportForm({ ...importForm, target_agent_id: e.target.value })}
                      style={{ ...inputStyle, marginBottom: 12 }}>
                      <option value="">Choose an agent…</option>
                      {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>

                    <div>
                      <button onClick={doImport} style={btnStyle(GREEN)}>Install instance</button>
                      {importForm.body_md && !importForm.schema_hash && (
                        <span style={{ fontSize: 11, color: AMBER, marginLeft: 10 }}>
                          The file name didn't include a schema hash — re-export it, or rename it to <code>skill_&lt;name&gt;_&lt;hash&gt;.md</code>.
                        </span>
                      )}
                    </div>
                  </div>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                  <div>
                    <div style={{ color: MUTED, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                      Attached instances
                    </div>
                    {instances.length === 0 && (
                      <div style={{ ...cardStyleSmall(), color: MUTED, fontSize: 13 }}>
                        No skill instances attached yet. Use the Discover tab to auto-match against an agent's data.
                      </div>
                    )}
                    {instances.map((inst) => (
                      <div
                        key={inst.id}
                        onClick={() => loadPending(inst)}
                        style={{
                          padding: 12,
                          border: `1px solid ${selectedInstance?.id === inst.id ? BLUE : BORDER}`,
                          borderRadius: 6, marginBottom: 8,
                          background: selectedInstance?.id === inst.id ? ACCENT_SOFT : CARD_BG,
                          cursor: "pointer",
                        }}
                      >
                        <div style={{ fontWeight: 500 }}>
                          {inst.base_name} v{inst.base_version}
                          {statusBadge(inst.validation_status)}
                        </div>
                        <div style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>
                          agent {agents.find((a) => a.id === inst.agent_id)?.name || `${String(inst.agent_id).slice(0, 8)} (unknown)`} · hash {inst.schema_hash} · {(inst.body_md || "").length} chars
                        </div>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div style={{ color: MUTED, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                      Instance detail
                    </div>
                    {selectedInstance ? (
                      <div style={cardStyle()}>
                        <h3 style={{ margin: "0 0 12px", fontSize: 16 }}>{selectedInstance.base_name}</h3>
                        <div style={{ fontSize: 12, color: MUTED, marginBottom: 8 }}>
                          Instance {String(selectedInstance.id).slice(0, 12)}...
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "4px 12px", fontSize: 12, marginBottom: 12 }}>
                          <div style={{ color: MUTED }}>Version</div><div>{selectedInstance.base_version}</div>
                          <div style={{ color: MUTED }}>Schema hash</div><div style={{ fontFamily: "monospace" }}>{selectedInstance.schema_hash}</div>
                          <div style={{ color: MUTED }}>Validation</div><div>{statusBadge(selectedInstance.validation_status)}</div>
                          <div style={{ color: MUTED }}>User-edited</div><div>{selectedInstance.user_edited ? "yes" : "no"}</div>
                          {selectedInstance.impact_stats && (
                            <>
                              <div style={{ color: MUTED }}>Questions</div><div>{selectedInstance.impact_stats.questions_answered ?? "—"}</div>
                              <div style={{ color: MUTED }}>Examples applied</div><div>{selectedInstance.impact_stats.examples_applied ?? "—"}</div>
                            </>
                          )}
                        </div>
                        <details>
                          <summary style={{ cursor: "pointer", color: MUTED, fontSize: 12 }}>
                            body_md ({(selectedInstance.body_md || "").length} chars)
                          </summary>
                          <pre style={{ background: CARD_ALT, padding: 10, borderRadius: 4, fontSize: 10, overflow: "auto", maxHeight: 300, marginTop: 8 }}>
                            {(selectedInstance.body_md || "").slice(0, 4000)}
                          </pre>
                        </details>
                        <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const res = await skillsApi.exportInstance(selectedInstance.id);
                                const blob = await res.blob();
                                const filename = `skill_${selectedInstance.base_name}_${selectedInstance.schema_hash}.md`;
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = filename;
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                URL.revokeObjectURL(url);
                              } catch (e) {
                                notify(`Export failed: ${e.message}`, "error");
                              }
                            }}
                            style={btnStyle(BLUE)}
                          >
                            Export .md ↓
                          </button>
                          <button type="button" onClick={doDiff} disabled={diffBusy} style={btnStyle(AMBER)}>
                            {diffBusy ? "Diffing…" : "Diff vs base"}
                          </button>
                          <button type="button" onClick={doRebase} style={btnStyle(BLUE)}>
                            Rebase to current base
                          </button>
                          <button type="button" onClick={() => { setCloneOpen((v) => !v); setDetachConfirm(false); }} style={btnStyle(GREEN)}>
                            Clone to agent…
                          </button>
                          <button type="button" onClick={doRefresh} style={btnStyle(MUTED)}>
                            Refresh
                          </button>
                          <button type="button" onClick={() => { setCopyOpen((v) => !v); setCloneOpen(false); setDetachConfirm(false); }} style={btnStyle(BLUE)}>
                            Copy to tenant…
                          </button>
                          {detachConfirm ? (
                            <>
                              <button type="button" onClick={doDetach} style={btnStyle(RED)}>Confirm detach</button>
                              <button type="button" onClick={() => setDetachConfirm(false)} style={btnStyle(MUTED)}>Cancel</button>
                            </>
                          ) : (
                            <button type="button" onClick={() => { setDetachConfirm(true); setCloneOpen(false); }} style={btnStyle(RED)}>
                              Detach
                            </button>
                          )}
                        </div>

                        {/* Inline clone-to-agent picker (replaces prompt()) */}
                        {cloneOpen && (
                          <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                            <select
                              aria-label="Clone to agent"
                              value={cloneTarget}
                              onChange={(e) => setCloneTarget(e.target.value)}
                              style={{ ...inputStyle, width: "auto", minWidth: 200 }}
                            >
                              <option value="">Choose an agent…</option>
                              {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                            </select>
                            <button type="button" onClick={doClone} disabled={!cloneTarget} style={btnStyle(GREEN)}>Clone</button>
                            <button type="button" onClick={() => { setCloneOpen(false); setCloneTarget(""); }} style={btnStyle(MUTED)}>Cancel</button>
                          </div>
                        )}

                        {/* Inline copy-to-tenant (admin cross-tenant copy) */}
                        {copyOpen && (
                          <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                            <input
                              aria-label="Target tenant id"
                              placeholder="Target tenant id"
                              value={copyTenant}
                              onChange={(e) => setCopyTenant(e.target.value)}
                              style={{ ...inputStyle, width: "auto", minWidth: 220 }}
                            />
                            <input
                              aria-label="Target agent id (optional)"
                              placeholder="Target agent id (optional)"
                              value={copyAgent}
                              onChange={(e) => setCopyAgent(e.target.value)}
                              style={{ ...inputStyle, width: "auto", minWidth: 220 }}
                            />
                            <button type="button" onClick={doCopyToTenant} disabled={!copyTenant.trim()} style={btnStyle(BLUE)}>Copy</button>
                            <button type="button" onClick={() => { setCopyOpen(false); setCopyTenant(""); setCopyAgent(""); }} style={btnStyle(MUTED)}>Cancel</button>
                          </div>
                        )}

                        {diff && (
                          <div style={{ marginTop: 12 }}>
                            <div style={{ fontSize: 12, color: MUTED, marginBottom: 6 }}>
                              Diff: base <strong>{selectedInstance.base_name}</strong> (current) vs this instance
                              {"  "}
                              <button onClick={() => setDiff(null)} style={{ ...btnStyle(MUTED), marginLeft: 8 }}>close</button>
                            </div>
                            <pre style={{ background: CARD_ALT, padding: 10, borderRadius: 4, fontSize: 10, overflow: "auto", maxHeight: 400 }}>
                              {diff.map((d, i) => (
                                <div key={i} style={{
                                  background: d.type === "add" ? `${GREEN}18` : d.type === "del" ? `${RED}18` : "transparent",
                                  color: d.type === "add" ? GREEN : d.type === "del" ? RED : undefined,
                                }}>
                                  {d.type === "add" ? "+ " : d.type === "del" ? "- " : "  "}{d.line}
                                </div>
                              ))}
                            </pre>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ ...cardStyleSmall(), color: MUTED, fontSize: 13 }}>
                        Select an instance to view details.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {tab === "discover" && (
              <div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 12, color: MUTED, marginRight: 8 }}>Agent:</label>
                  <select
                    value={selectedAgent}
                    onChange={(e) => setSelectedAgent(e.target.value)}
                    style={{ padding: "6px 8px", fontSize: 12, borderRadius: 4, border: `1px solid ${BORDER}`, background: COLORS.surface, color: COLORS.text }}
                  >
                    <option value="">Select agent…</option>
                    {agents.map((a) => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                  <button
                    onClick={runDiscover}
                    disabled={!selectedAgent || busy}
                    style={{
                      marginLeft: 8, padding: "6px 12px", fontSize: 12,
                      background: BLUE, color: "white", border: "none", borderRadius: 4, cursor: "pointer",
                      opacity: !selectedAgent || busy ? 0.5 : 1,
                    }}
                  >
                    {busy ? "Running…" : "Run matcher"}
                  </button>
                </div>
                {discoverRan && matches.length === 0 && !busy && (
                  <div style={{ padding: 14, border: `1px dashed ${BORDER}`, borderRadius: 6, color: MUTED, fontSize: 13, lineHeight: 1.6 }}>
                    <strong>No matches for this agent.</strong> The matcher is <em>data-driven</em> — it only
                    matches a skill to <em>data the agent actually has</em> (its <span style={{ fontFamily: "monospace" }}>data_catalog</span> tables
                    or documents). This agent has no matching data yet, so nothing auto-matches.
                    <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
                      <li>To get data matches, ingest a dataset for this agent (Agent → <strong>Knowledge → Upload</strong>). The built-in skills fit loan / telco / prices / financing data.</li>
                      <li>To attach <em>any</em> skill by hand regardless of data, go to the agent's <strong>Overview → Skills → ＋ Add a skill manually</strong>.</li>
                    </ul>
                  </div>
                )}
                {matches.length > 0 && (
                  <div>
                    <div style={{ color: MUTED, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                      Matched skills ({matches.length})
                    </div>
                    {matches.map((m, i) => (
                      <div key={i} style={{ padding: 12, border: `1px solid ${BORDER}`, borderRadius: 6, marginBottom: 8, display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ flex: 1 }}>
                          <div>
                            <strong>{m.skill_name}</strong>
                            {actionBadge(m.action)}
                          </div>
                          <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>
                            table <span style={{ fontFamily: "monospace" }}>{m.table_name || "?"}</span>
                            {" · "}score <span style={{ fontFamily: "monospace" }}>{Number(m.score).toFixed(3)}</span>
                            {" · "}via {m.matched_via}
                          </div>
                        </div>
                        <button onClick={() => doAttach(m)} disabled={busy || !m.table_name} style={btnStyle(GREEN)}>
                          Attach
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {tab === "base" && (
              <div>
                <div style={{ marginBottom: 12 }}>
                  <button onClick={() => setShowUpload((v) => !v)} style={btnStyle(BLUE)}>
                    {showUpload ? "Cancel upload" : "Upload custom skill"}
                  </button>
                </div>
                {showUpload && (
                  <div style={{ ...cardStyle(), marginBottom: 16 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>
                      Upload a tenant-scoped base skill (visibility capped at tenant/private)
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
                      <input placeholder="name (kebab-case)" value={uploadForm.name}
                        onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })} style={inputStyle} />
                      <input placeholder="version" value={uploadForm.version}
                        onChange={(e) => setUploadForm({ ...uploadForm, version: e.target.value })} style={inputStyle} />
                      <select value={uploadForm.visibility}
                        onChange={(e) => setUploadForm({ ...uploadForm, visibility: e.target.value })} style={inputStyle}>
                        <option value="tenant">tenant</option>
                        <option value="private">private</option>
                      </select>
                    </div>
                    <div style={{ fontSize: 11, color: MUTED, marginBottom: 4 }}>
                      Frontmatter (JSON — applies_to.required_columns drives the matcher):
                    </div>
                    <textarea rows={4} value={uploadForm.frontmatter}
                      onChange={(e) => setUploadForm({ ...uploadForm, frontmatter: e.target.value })}
                      style={{ ...inputStyle, fontFamily: "monospace", fontSize: 11, marginBottom: 8 }} />
                    <div style={{ fontSize: 11, color: MUTED, marginBottom: 4 }}>
                      Body (.md with ## System prompt fragment / ## Business rules / ## SQL few-shots …):
                    </div>
                    <textarea rows={10} value={uploadForm.body_md}
                      onChange={(e) => setUploadForm({ ...uploadForm, body_md: e.target.value })}
                      style={{ ...inputStyle, fontFamily: "monospace", fontSize: 11 }} />
                    <div style={{ marginTop: 8 }}>
                      <button onClick={doUploadBase} style={btnStyle(GREEN)}>Upload</button>
                    </div>
                  </div>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                  <div>
                    <div style={{ color: MUTED, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                      Base skills ({baseSkills.length})
                    </div>
                    {baseSkills.map((b) => (
                      <div key={b.name}
                        onClick={async () => {
                          try { setSelectedBase(await skillsApi.getBase(b.name)); }
                          catch (e) { setErr(String(e.message || e)); }
                        }}
                        style={{
                          padding: 12,
                          border: `1px solid ${selectedBase?.name === b.name ? BLUE : BORDER}`,
                          borderRadius: 6, marginBottom: 8, cursor: "pointer",
                          background: selectedBase?.name === b.name ? ACCENT_SOFT : CARD_BG,
                        }}>
                        <div style={{ fontWeight: 500 }}>
                          {b.name} <span style={{ color: MUTED, fontWeight: 400 }}>v{b.version}</span>
                          {badge(b.visibility || "global", b.visibility === "global" ? BLUE : AMBER)}
                          {statusBadge(b.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div style={{ color: MUTED, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                      Base detail
                    </div>
                    {selectedBase ? (
                      <div style={cardStyle()}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 8 }}>
                          <h3 style={{ margin: 0, fontSize: 16 }}>
                            {selectedBase.name} v{selectedBase.version} {statusBadge(selectedBase.status)}
                          </h3>
                          {/* Drafts (imported or authored) have no other route to the
                              publish flow — open the Author edit surface (validate →
                              preview impact → Publish). Own-tenant skills only. */}
                          {selectedBase.visibility !== "global" && (
                            <button
                              onClick={() => _navigate(`/skills?tab=author&edit=${encodeURIComponent(selectedBase.name)}`)}
                              style={btnStyle(GREEN)}
                            >✎ Edit / Publish</button>
                          )}
                        </div>
                        <pre style={{ background: CARD_ALT, padding: 10, borderRadius: 4, fontSize: 10, overflow: "auto", maxHeight: 460 }}>
                          {(selectedBase.body_md || "").slice(0, 12000)}
                        </pre>
                      </div>
                    ) : (
                      <div style={{ ...cardStyleSmall(), color: MUTED, fontSize: 13 }}>
                        Select a base skill to read its .md.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {tab === "review" && (
              <div>
                {!selectedInstance && (
                  <div style={{ ...cardStyleSmall(), color: MUTED, fontSize: 13 }}>
                    Select an instance from the <strong>Instances</strong> tab first, then pending examples for that instance appear here.
                  </div>
                )}
                {selectedInstance && (
                  <div>
                    <div style={{ marginBottom: 12, fontSize: 12, color: MUTED }}>
                      Pending examples for <strong>{selectedInstance.base_name}</strong> ({pending.length})
                    </div>
                    {pending.length === 0 && (
                      <div style={{ ...cardStyleSmall(), color: MUTED, fontSize: 13 }}>
                        No pending examples on this instance.
                      </div>
                    )}
                    {pending.map((ex) => (
                      <div key={ex.id} style={{ padding: 12, border: `1px solid ${BORDER}`, borderRadius: 6, marginBottom: 8 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                          <strong style={{ fontSize: 13 }}>{ex.source_failure_id || "(no source)"}</strong>
                          {statusBadge(ex.status)}
                          <span style={{ marginLeft: "auto", fontSize: 12, color: MUTED }}>
                            confidence <span style={{ fontFamily: "monospace" }}>{Number(ex.confidence).toFixed(2)}</span>
                          </span>
                        </div>
                        <div style={{ fontSize: 12, marginBottom: 6 }}>{ex.question}</div>
                        <details>
                          <summary style={{ cursor: "pointer", fontSize: 12, color: MUTED }}>Preview SQL</summary>
                          <pre style={{ background: CARD_ALT, padding: 8, borderRadius: 4, fontSize: 10, overflow: "auto", marginTop: 6 }}>
                            {ex.sql}
                          </pre>
                        </details>
                        {ex.status === "pending" && (
                          rejectFor === ex.id ? (
                            <div style={{ marginTop: 8, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                              <input
                                aria-label="Reject reason"
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                placeholder="Reject reason"
                                style={{ ...inputStyle, width: "auto", minWidth: 200 }}
                              />
                              <button onClick={() => doReject(ex)} style={btnStyle(RED)}>Confirm reject</button>
                              <button onClick={() => setRejectFor(null)} style={btnStyle(MUTED)}>Cancel</button>
                            </div>
                          ) : (
                            <div style={{ marginTop: 8, display: "flex", gap: 6 }}>
                              <button onClick={() => doApply(ex)} style={btnStyle(GREEN)}>Apply</button>
                              <button onClick={() => { setRejectFor(ex.id); setRejectReason("not useful"); }} style={btnStyle(RED)}>Reject</button>
                            </div>
                          )
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
